import { Cards } from "components";
import { useGetQuery } from "hooks";
import { useGeolocation, useStateValue } from "providers";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled, { useTheme } from "styled-components";
import { MapFilters } from "./MapFilters";
import { LeafletMap, useMapInstance } from "./LeafletMap";
import { PlacesLayer } from "./PlacesLayer";
import { WallLayer } from "./WallLayer";
import { MapToggleButton } from "./ToggleButton";

const DEFAULT_CENTER = { lat: 41.9168256, lng: -87.6969984 };

const DARK_TILE_URL = 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png';
const LIGHT_TILE_URL = 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png';

const MapContainer = styled.div`
  position: relative;
  height: 100%;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 1rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 99999;
  padding: 0.4rem 1rem;
  border-radius: 1rem;
  font-size: 0.85rem;
  pointer-events: none;
  ${({ theme: { colors } }) => `
    background: ${colors('chrome')};
    color: ${colors('text')};
  `}
`;

// Handles geolocation panning and map-move refetch. Must be inside <LeafletMap>.
const PlacesMapController = ({ position, loading, onMoveEnd }) => {
  const map = useMapInstance();
  const pannedRef = useRef(false);

  useEffect(() => {
    if (!map || loading || pannedRef.current || !position?.latitude) return;
    pannedRef.current = true;
    map.panTo([position.latitude, position.longitude]);
  }, [map, loading, position?.latitude, position?.longitude]);

  useEffect(() => {
    if (!map || !onMoveEnd) return;
    map.on('moveend', onMoveEnd);
    return () => map.off('moveend', onMoveEnd);
  }, [map, onMoveEnd]);

  return null;
};

const WallToggle = styled(MapToggleButton)`
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 9999;
`;

// Simple embed map for a list of address locations.
export const PlacesMap = ({ locations, zoom = 14, className, style }) => (
  <div style={{ height: '30vh', ...style }} className={className}>
    <LeafletMap zoom={zoom}>
      <PlacesLayer places={locations} />
    </LeafletMap>
  </div>
);

export const GoogleMap = ({ children, showBackButton = true }) => {
  const [cards, setCards] = useState([]);
  const [filters, setFilters] = useState('');
  const [showWall, setShowWall] = useState(false);
  const navigate = useNavigate();
  const { position, loading } = useGeolocation();
  const [{ global: { theme = 'default', variant } = {} }] = useStateValue();
  const scTheme = useTheme();

  const resolvedVariant = variant ?? 0;
  const tileUrl = resolvedVariant === 0 ? DARK_TILE_URL : LIGHT_TILE_URL;
  const overlayColor = theme === 'fall' ? scTheme.colors('link', 0.18) : null;
  const overlayBlendMode = resolvedVariant === 0 ? 'multiply' : 'soft-light';

  const buildUrl = (lat, lng, filterString) => {
    const rLat = Math.round(lat * 1e3) / 1e3;
    const rLng = Math.round(lng * 1e3) / 1e3;
    const base = `/places?lat=${rLat}&lng=${rLng}`;
    return filterString ? `${base}&filters=${filterString}` : base;
  };

  const { data, isFetching, setEnabled, setPath } = useGetQuery({
    enabled: !!position?.latitude,
    url: buildUrl(position?.latitude, position?.longitude, filters),
  });

  const idleTimer = useRef(null);
  const lastFetchCenter = useRef(null);
  const filtersRef = useRef(filters);
  useEffect(() => { filtersRef.current = filters; }, [filters]);

  const updateCards = useCallback((card) => {
    setCards(prev => {
      const idx = prev.indexOf(card);
      if (idx === -1) return prev;
      const next = [...prev];
      const [place] = next.splice(idx, 1);
      next.unshift(place);
      return next;
    });
  }, []);

  const handleFilterChange = useCallback((filterString) => {
    setFilters(filterString);
    if (position?.latitude) {
      setPath(buildUrl(position.latitude, position.longitude, filterString));
    }
  }, [position?.latitude, position?.longitude]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!position?.latitude) return;
    lastFetchCenter.current = { lat: position.latitude, lng: position.longitude };
    setEnabled(true);
    setPath(buildUrl(position.latitude, position.longitude, filters));
  }, [position?.latitude, position?.longitude]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setCards(data?.addresses?.rows || cards);
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMoveEnd = useCallback((e) => {
    const center = e.target.getCenter();
    const lat = center.lat;
    const lng = center.lng;
    const last = lastFetchCenter.current;
    if (last && Math.abs(lat - last.lat) < 0.001 && Math.abs(lng - last.lng) < 0.001) return;
    clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => {
      lastFetchCenter.current = { lat, lng };
      setPath(buildUrl(lat, lng, filtersRef.current));
    }, 1000);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <MapContainer>
      {(loading || isFetching) && <LoadingOverlay>Loading places…</LoadingOverlay>}
      <LeafletMap
        center={DEFAULT_CENTER}
        zoom={17}
        tileUrl={tileUrl}
        overlayColor={overlayColor}
        overlayBlendMode={overlayBlendMode}
      >
        <PlacesMapController position={position} loading={loading} onMoveEnd={handleMoveEnd} />
        <PlacesLayer places={data?.addresses?.rows} onMarkerClick={updateCards} />
        <WallLayer
          active={showWall}
          onTileClick={() => navigate('/games/wall')}
        />
        <Cards cardData={cards} />
        {children}
        <WallToggle $active={showWall} onClick={() => setShowWall((v) => !v)}>
          {showWall ? 'wall on' : 'wall'}
        </WallToggle>
      </LeafletMap>
      <MapFilters onChange={handleFilterChange} showBackButton={showBackButton} />
    </MapContainer>
  );
};
